
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AutomationSettings {
  frequency: string;
  notifyByEmail: boolean;
  autoClean: boolean;
  enabled: boolean;
  lastRun?: string;
}

export const useAutomationSettings = () => {
  const [lastError, setLastError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch automation settings from workflows table (personal workflows only)
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['automation-settings'],
    queryFn: async (): Promise<AutomationSettings> => {
      console.log('Fetching automation settings...');
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No session found, returning default settings');
          return {
            frequency: 'daily',
            notifyByEmail: false,
            autoClean: true,
            enabled: false
          };
        }

        // Get automation workflow for current user (personal workflows only to avoid team RLS issues)
        const { data: workflows, error } = await supabase
          .from('workflows')
          .select('*')
          .eq('created_by', session.user.id)
          .eq('name', 'Master Automation Control')
          .is('team_id', null) // Explicitly filter for personal workflows only
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching automation settings:', error);
          setLastError(error.message);
          // Return defaults instead of throwing to prevent UI breakage
          return {
            frequency: 'daily',
            notifyByEmail: false,
            autoClean: true,
            enabled: false
          };
        }

        if (workflows && workflows.length > 0) {
          const workflow = workflows[0];
          let config = workflow.config;
          
          // Properly handle config parsing with type safety
          let parsedConfig: any = {};
          if (typeof config === 'string') {
            try {
              parsedConfig = JSON.parse(config);
            } catch (e) {
              console.error('Error parsing config:', e);
              parsedConfig = {};
            }
          } else if (config && typeof config === 'object') {
            parsedConfig = config;
          }

          console.log('Found automation settings:', parsedConfig);
          
          return {
            frequency: parsedConfig.frequency || 'daily',
            notifyByEmail: parsedConfig.notifyByEmail || false,
            autoClean: parsedConfig.autoClean || true,
            enabled: workflow.status === 'active',
            lastRun: workflow.updated_at
          };
        }

        // Return default settings if no workflow found
        console.log('No automation workflow found, returning defaults');
        return {
          frequency: 'daily',
          notifyByEmail: false,
          autoClean: true,
          enabled: false
        };
      } catch (error) {
        console.error('Error in automation settings query:', error);
        setLastError(error instanceof Error ? error.message : 'Unknown error');
        // Return defaults instead of throwing to prevent UI breakage
        return {
          frequency: 'daily',
          notifyByEmail: false,
          autoClean: true,
          enabled: false
        };
      }
    },
    retry: 1, // Reduce retries to prevent error loops
    staleTime: 30000,
    // Always return data, even on error
    throwOnError: false,
  });

  // Update automation settings
  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: async (newSettings: AutomationSettings) => {
      console.log('Updating automation settings:', newSettings);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('User not authenticated');
        }

        // Check if automation workflow exists (personal workflows only)
        const { data: existingWorkflows } = await supabase
          .from('workflows')
          .select('id')
          .eq('created_by', session.user.id)
          .eq('name', 'Master Automation Control')
          .is('team_id', null) // Explicitly filter for personal workflows only
          .limit(1);

        const config = {
          frequency: newSettings.frequency,
          notifyByEmail: newSettings.notifyByEmail,
          autoClean: newSettings.autoClean,
          lastUpdated: new Date().toISOString()
        };

        if (existingWorkflows && existingWorkflows.length > 0) {
          // Update existing workflow
          const { data, error } = await supabase
            .from('workflows')
            .update({
              config,
              status: newSettings.enabled ? 'active' : 'inactive',
              updated_at: new Date().toISOString()
            })
            .eq('id', existingWorkflows[0].id)
            .eq('created_by', session.user.id)
            .select();

          if (error) {
            console.error('Error updating automation settings:', error);
            throw error;
          }

          console.log('Automation settings updated:', data);
          return data;
        } else {
          // Create new workflow
          const { data, error } = await supabase
            .from('workflows')
            .insert({
              name: 'Master Automation Control',
              config,
              status: newSettings.enabled ? 'active' : 'inactive',
              created_by: session.user.id,
              team_id: null // Explicitly set as personal workflow
            })
            .select();

          if (error) {
            console.error('Error creating automation settings:', error);
            throw error;
          }

          console.log('Automation settings created:', data);
          return data;
        }
      } catch (error) {
        console.error('Error in mutation:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('Automation settings saved successfully');
      toast.success('Automation settings saved successfully');
      queryClient.invalidateQueries({ queryKey: ['automation-settings'] });
      // Clear any previous errors
      setLastError(null);
    },
    onError: (error) => {
      console.error('Error saving automation settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setLastError(errorMessage);
      toast.error('Failed to save automation settings: ' + errorMessage);
    }
  });

  return {
    settings,
    isLoading,
    updateSettings,
    isPending,
    error: lastError || (error instanceof Error ? error.message : error)
  };
};
